import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";

// third-party
import PerfectScrollbar from "react-perfect-scrollbar";

// project imports
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";

// assets
import { IconLock, IconLogout, IconSettings } from "@tabler/icons";
import { color } from "utils/color";
import API from "api/axios";
import { toast } from "react-toastify";
import ApiRoutes from "api/endpoint";

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = ({ name, email, type }) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleLogout = async () => {
    try {
      await API.request({
        url: ApiRoutes.Auth.Logout.Endpoint,
        method: ApiRoutes.Auth.Logout.Method,
      });
      toast.success("Logged out!!!");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.clear();
      navigate("/");
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleListItemClick = (event, index, route = "") => {
    setSelectedIndex(index);
    handleClose(event);
    if (route) navigate(route);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current && !open) anchorRef.current?.focus();
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Chip
        sx={{
          width: 100,
          height: 48,
          borderRadius: "27px",
          borderColor: color.fg,
          backgroundColor: color.bg,
          color: color.fg,
          transition: "all .2s ease-in-out",
          "&:hover": {
            backgroundColor: color.fg,
            color: color.bg,
            "& svg": { stroke: color.bg },
          },
        }}
        icon={
          <Avatar
            ref={anchorRef}
            sx={{
              ...theme.typography.mediumAvatar,
              cursor: "pointer",
              backgroundColor: color.fg,
              color: color.bg,
            }}
          />
        }
        label={<IconSettings stroke={1.5} size="1.5rem" />}
        variant="outlined"
        onClick={handleToggle}
      />

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        modifiers={[{ name: "offset", options: { offset: [0, 14] } }]}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper sx={{ width: 280, borderRadius: 2, border: `1px solid ${color.bg}` }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="h4">
                        Welcome Back, {name || (type === "Admin" ? "Admin" : "User")}
                      </Typography>
                      <Typography variant="body2">{email}</Typography>
                    </Stack>
                  </Box>

                  <PerfectScrollbar style={{ maxHeight: "calc(100vh - 250px)" }}>
                    {/* Change Password */}
                    <Box sx={{ px: 2 }}>
                      <button
                        className="changePassWordBtn"
                        onClick={(e) => handleListItemClick(e, 3, "/change-password")}
                      >
                        <IconLock size="1.3rem" />
                        <span>Change Password</span>
                      </button>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <List>
                        <ListItemButton
                          selected={selectedIndex === 4}
                          onClick={handleLogout}
                          sx={{
                            borderRadius: `${customization.borderRadius}px`,
                          }}
                        >
                          <ListItemIcon>
                            <IconLogout size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary="Logout" />
                        </ListItemButton>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
